/* eslint-disable formatjs/no-literal-string-in-jsx */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';
import { InterPoint, Logger } from '@saviynt/common';
import {
  ApplicationIcon,
  Badge,
  Box,
  Button,
  ButtonSelect,
  Checkbox,
  FooterActionBar,
  Icon,
  Illustration,
  InlineMessage,
  InputField,
  Link as DSLink,
  ListRow,
  MenuMulti,
  type MenuMultiOption,
  ModalIsland,
  ModalPageHeader,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@saviynt/design-system';

import { ListSkeleton } from '../../../../shared/components/ListViewer';
import {
  CF_CRT_MED,
  CF_NEU_BLD,
  CF_NEU_STL,
  CHAR_LIMIT_FOR_SEARCH,
  F_KEY_AP,
  F_KEY_ON,
  F_KEY_ST,
  l,
  type MessageState,
} from '../../../../shared/constants';
import {
  useDebounce,
  useDebouncedFn,
  useEffectAfterMount,
  useInViewObserver,
} from '../../../../shared/hooks';
import {
  type ServiceAccountState,
  type ServiceIdentityFilters,
  type ServiceIdSPItemSA,
} from '../../../../shared/types';
import { getLogoEndpointUrl } from '../../../../shared/utils';
import {
  useGetOwnersListMutation,
  useGetServiceAccountListMutation,
  useLazyGetApplicationListQuery,
} from '../../data-layer/serviceIdentityApi';
import { type ApplicationApiResponse } from '../../data-layer/serviceIdentityType';
import { type OwnerApiResponse } from '../../hooks';
import { getNoDataText, getStatusKind } from '../../utils';

const MAX_ACCOUNT_ASSIGNMENT = 25;

interface ServiceAccountListItemState {
  accountKey: number;
  name: string;
  description: string | null;
  accountType: string;
  status: string;
  endpointKey: number;
  endpointName: string;
  endpointLogo: string | null;
  owners: {
    name: string;
    rank: number;
    type: string;
  }[];
  isChecked: boolean;
}

interface TrackedListItem extends ServiceAccountListItemState {
  tracker: {
    id: number;
    isChecked: boolean;
  };
}

interface ServiceAccountListItemProps {
  intl: IntlShape;
  isLoading: boolean;
  listItem: TrackedListItem;
  assignedCount: number;
  handleClick: (id: number) => void;
}

/**
 * Renders a single service account row with checkbox, endpoint logo, and description.
 *
 * @param props - {@link ServiceAccountListItemProps}
 * @param props.isLoading - Disables the checkbox when true.
 * @param props.listItem - The tracked service account data to display.
 * @param props.handleClick - Callback invoked with the tracker id on checkbox toggle.
 * @returns A {@link ListRow} element representing the service account.
 */
const ServiceAccountListItem = React.memo(function ServiceAccountListItem({
  intl,
  isLoading,
  listItem,
  assignedCount,
  handleClick,
}: ServiceAccountListItemProps) {
  const {
    name,
    endpointLogo,
    endpointName,
    description,
    status,
    tracker: { id, isChecked },
  } = listItem;

  const isLimit = Boolean(assignedCount >= MAX_ACCOUNT_ASSIGNMENT);
  const isDisabled = useMemo(() => {
    if ((isLimit && !isChecked) || isLoading) {
      return true;
    }

    return false;
  }, [isChecked, isLimit, isLoading]);

  const onCheckBoxChange = useCallback(() => {
    handleClick(id);
  }, [handleClick, id]);

  const { statusColor, statusTextKey, statusText } = getStatusKind(status);

  return (
    <ListRow
      isCheckboxDisabled={isDisabled}
      leadingComp={
        !isDisabled ? (
          <ApplicationIcon logo='Account' size='medium' />
        ) : (
          <div className='ServiceAccountListItem-leadingComp--isDisabled'>
            <Checkbox size='small' isDisabled isIndeterminate />
          </div>
        )
      }
      centerContent={
        <Box tag='div' className='ServiceAccountListItem-contentWrapper'>
          <Typography kind='h3' color={CF_NEU_BLD}>
            {name}
          </Typography>
          <Box tag='div' className='ServiceAccountListItem-content'>
            <Box
              tag='div'
              className='ServiceAccountListItem-contentApplication'>
              <ApplicationIcon
                size='xxSmall'
                src={getLogoEndpointUrl(endpointLogo ?? undefined)}
              />
              <Typography kind='body2' color={CF_NEU_BLD} lineLimit={1}>
                {endpointName}
              </Typography>
            </Box>
            {description && description.trim().length > 0 ? (
              <>
                <InterPoint />
                <Typography kind='body2' color={CF_NEU_STL} lineLimit={1}>
                  {description}
                </Typography>
              </>
            ) : null}
          </Box>
        </Box>
      }
      isChecked={isChecked}
      trailingContent={
        statusText.trim().length > 0 ? (
          <Typography
            kind='body2'
            color={statusColor}
            className='ServiceAccountListItem-trailingWrapper'>
            {`● ${intl.formatMessage(l[statusTextKey])}`}
          </Typography>
        ) : undefined
      }
      handleCheckBoxChange={onCheckBoxChange}
    />
  );
});

interface LinkAccountActionProps {
  serviceId?: number;
  isModalOpen: boolean;
  handleModalClose: () => void;
  handleSubmit: ({
    addAccounts,
    removeAccounts,
  }: {
    addAccounts?: ServiceAccountState[];
    removeAccounts?: ServiceIdSPItemSA[];
    finalList: ServiceAccountState[];
  }) => void;
  isLoading: boolean;
  isDisabled?: boolean;
  currentServiceAccounts: ServiceIdSPItemSA[];
}

const DEFAULT_LIST_STATE = {
  cursor: null as string | null,
  totalCount: null as number | null,
  isObserverInView: true,
  isListError: false,
  isListLoading: false,
};

/**
 * Modal for linking service accounts to a service identity. Provides search,
 * filtering (status, owners), and infinite-scroll pagination over available
 * accounts with checkbox selection.
 *
 * @param props - {@link LinkAccountActionProps}
 * @param props.serviceId - The service identity ID to link accounts to.
 * @param props.isModalOpen - Controls modal visibility.
 * @param props.handleModalClose - Callback to close the modal.
 * @param props.handleSubmit - Callback invoked on save with added, removed, and final account lists.
 * @param props.isLoading - Shows loading state and disables interactions.
 * @param props.isDisabled - Disables interactive controls when true.
 * @param props.currentServiceAccounts - Already-assigned service accounts.
 * @returns A {@link ModalIsland} with account search, filters, and selection list.
 */
function LinkAccountAction({
  serviceId,
  isModalOpen,
  handleModalClose,
  handleSubmit,
  isLoading,
  isDisabled,
  currentServiceAccounts,
}: LinkAccountActionProps) {
  // Hooks
  const intl = useIntl();

  // States
  // Controls
  const [assignType, setAssignType] = useState(1);
  const [isFilterBar, setIsFilterBar] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState<ServiceIdentityFilters[]>([
    {
      label: 'label.status',
      value: F_KEY_ST,
      options: [
        { label: 'status.active', value: 'active', isChecked: false },
        { label: 'status.inactive', value: 'inactive', isChecked: false },
      ],
      searchText: '',
      isLoading: false,
      isError: false,
      isApiBased: false,
      hasMoreOptions: false,
    },
    {
      label: 'label.owners',
      value: F_KEY_ON,
      options: [],
      searchText: '',
      isLoading: false,
      isError: false,
      isApiBased: true,
      hasMoreOptions: false,
    },
    {
      label: 'label.applications',
      value: F_KEY_AP,
      options: [],
      searchText: '',
      isLoading: false,
      isError: false,
      isApiBased: true,
      hasMoreOptions: false,
    },
  ]);
  const [activeFilters, setActiveFilters] = useState<
    Record<string, (string | number)[]>
  >({});
  const [searchText, setSearchText] = useState('');

  // List
  const [listState, setListState] = useState(DEFAULT_LIST_STATE);
  const [listRows, setListRows] = useState<TrackedListItem[]>([]);
  const [activeRows, setActiveRows] = useState<TrackedListItem[]>(
    currentServiceAccounts.map((sa) => ({
      accountKey: sa.id,
      name: sa.name,
      description: null,
      accountType: 'Service Account',
      status: sa.status,
      endpointKey: 0,
      endpointName: sa.endpoint,
      endpointLogo: sa.icon,
      owners: [],
      isChecked: true,
      tracker: {
        id: sa.id,
        isChecked: true,
      },
    }))
  );

  // Cursor state per filter key
  const cursorRef = useRef<Record<string, string | null>>({});

  // Ref to track latest filters without causing callback recreation
  const filtersRef = useRef(filters);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Constants
  const isBadgeIcon = useMemo(
    () => filters.some((f) => f.options.some((o) => o.isChecked)),
    [filters]
  );
  const isClrDisabled = useMemo(
    () => filters.some((f) => f.isLoading),
    [filters]
  );
  const isValidApiCall = useMemo(
    () =>
      Boolean(
        !listState.isListLoading &&
          (listState.totalCount === null || listState.cursor !== null)
      ),
    [listState.isListLoading, listState.totalCount, listState.cursor]
  );
  const isZeroCount = useMemo(
    () =>
      Boolean(
        listRows.length === 0 &&
          listState.totalCount !== null &&
          !isValidApiCall
      ),
    [isValidApiCall, listRows.length, listState.totalCount]
  );
  const isFilterApplied = useMemo(
    () =>
      searchText.trim().length > CHAR_LIMIT_FOR_SEARCH ||
      Object.keys(activeFilters).length > 0,
    [activeFilters, searchText]
  );

  // Hooks
  const [getOwnersList] = useGetOwnersListMutation();
  const [getApplicationsList] = useLazyGetApplicationListQuery();
  const [fetchList, { isError: isListError }] =
    useGetServiceAccountListMutation();

  const listObserverElement = useInViewObserver(({ isInView }) => {
    setListState((prev) => ({ ...prev, isObserverInView: isInView }));
  });

  const updateFilter = useCallback(
    (
      fk: string,
      patch: Partial<{
        options: MenuMultiOption[];
        isLoading: boolean;
        isError: boolean;
        hasMoreOptions: boolean;
      }>
    ) => {
      setFilters((prev) =>
        prev.map((f) => (f.value === fk ? { ...f, ...patch } : f))
      );
    },
    []
  );

  const loadOwnerFilterOptions = useCallback(
    (isInit: boolean) => {
      const fk = F_KEY_ON;
      const currentFilter = filtersRef.current.find((f) => f.value === fk);

      if (!currentFilter) {
        return;
      }

      const { searchText: currentSearchText } = currentFilter;
      const isAppend = !isInit && cursorRef.current[fk] != null;

      // Set loading state
      updateFilter(fk, { isLoading: true, isError: false });

      // On fresh search / init, reset cursor and keep only checked options
      if (isInit) {
        cursorRef.current[fk] = null;
        updateFilter(fk, {
          isLoading: true,
          isError: false,
          options: currentFilter.options.filter((o) => o.isChecked),
        });
      }

      getOwnersList({
        search: currentSearchText,
        max: 20,
        ...(cursorRef.current[fk] ? { cursor: cursorRef.current[fk] } : {}),
      })
        .unwrap()
        .then((response) => {
          const typedResponse = response as OwnerApiResponse;

          if (!typedResponse.success) {
            throw new Error(typedResponse.msg);
          }

          cursorRef.current[fk] = typedResponse.nextCursor;

          // Re-read current filter state for the latest checked options
          const latestFilter = filtersRef.current.find((f) => f.value === fk);
          const prevOptions = latestFilter?.options ?? [];
          const checkedOptions = prevOptions.filter((o) => o.isChecked);
          const checkedValues = new Set(checkedOptions.map((o) => o.value));

          const newOptions = typedResponse.data.map((owner) => ({
            label:
              owner.type === 'user'
                ? `${owner.firstname ?? ''} ${owner.lastname ?? ''} (${
                    owner.username
                  })`
                : owner.displayname ?? '',
            value: String(owner.id),
            isChecked: checkedValues.has(String(owner.id)),
          }));

          let mergedOptions: MenuMultiOption[];

          if (isAppend) {
            // Scroll: append new options, skip duplicates
            const existingValues = new Set(prevOptions.map((o) => o.value));
            const uniqueNew = newOptions.filter(
              (o) => !existingValues.has(o.value)
            );

            mergedOptions = [...prevOptions, ...uniqueNew];
          } else {
            // Search / init: checked first, then new unchecked
            const uncheckedNew = newOptions.filter(
              (o) => !checkedValues.has(o.value)
            );

            mergedOptions = [...checkedOptions, ...uncheckedNew];
          }

          updateFilter(fk, {
            options: mergedOptions,
            isLoading: false,
            hasMoreOptions: typedResponse.nextCursor !== null,
          });
        })
        .catch((error) => {
          cursorRef.current[fk] = null;
          updateFilter(fk, {
            isLoading: false,
            isError: true,
            hasMoreOptions: false,
          });

          Logger.log('Error loading owner filter options', error);
        });
    },
    [getOwnersList, updateFilter]
  );

  const loadApplicationFilterOptions = useCallback(
    (isInit: boolean) => {
      const fk = F_KEY_AP;
      const currentFilter = filtersRef.current.find((f) => f.value === fk);

      if (!currentFilter) {
        return;
      }

      const { searchText: currentSearchText } = currentFilter;
      const isAppend = !isInit && cursorRef.current[fk] != null;

      // Set loading state
      updateFilter(fk, { isLoading: true, isError: false });

      // On fresh search / init, reset cursor and keep only checked options
      if (isInit) {
        cursorRef.current[fk] = null;
        updateFilter(fk, {
          isLoading: true,
          isError: false,
          options: currentFilter.options.filter((o) => o.isChecked),
        });
      }

      getApplicationsList({
        wsearch: currentSearchText,
        type: 'SERVICE_ACCOUNT',
        max: 500,
        offset: 0,
      })
        .unwrap()
        .then((response) => {
          const typedResponse = response as ApplicationApiResponse;

          if (!typedResponse.success) {
            throw new Error(typedResponse.msg);
          }

          // Re-read current filter state for the latest checked options
          const latestFilter = filtersRef.current.find((f) => f.value === fk);
          const prevOptions = latestFilter?.options ?? [];
          const checkedOptions = prevOptions.filter((o) => o.isChecked);
          const checkedValues = new Set(checkedOptions.map((o) => o.value));

          const newOptions = typedResponse.data.map((a) => ({
            label: a.applicationName,
            value: String(a.applicationId),
            isChecked: checkedValues.has(String(a.applicationId)),
          }));

          let mergedOptions: MenuMultiOption[];

          if (isAppend) {
            // Scroll: append new options, skip duplicates
            const existingValues = new Set(prevOptions.map((o) => o.value));
            const uniqueNew = newOptions.filter(
              (o) => !existingValues.has(o.value)
            );

            mergedOptions = [...prevOptions, ...uniqueNew];
          } else {
            // Search / init: checked first, then new unchecked
            const uncheckedNew = newOptions.filter(
              (o) => !checkedValues.has(o.value)
            );

            mergedOptions = [...checkedOptions, ...uncheckedNew];
          }

          updateFilter(fk, {
            options: mergedOptions,
            isLoading: false,
            hasMoreOptions: false, // API does not support pagination, so we disable infinite scroll after the first fetch
          });
        })
        .catch((error) => {
          updateFilter(fk, {
            isLoading: false,
            isError: true,
            hasMoreOptions: false,
          });

          Logger.log('Error loading owner filter options', error);
        });
    },
    [getApplicationsList, updateFilter]
  );

  const loadFilterOptions = useCallback(
    (fk: string, isInit = false) => {
      switch (fk) {
        case F_KEY_ON:
          loadOwnerFilterOptions(isInit);
          break;
        case F_KEY_AP:
          loadApplicationFilterOptions(isInit);
          break;
        default:
          break;
      }
    },
    [loadOwnerFilterOptions, loadApplicationFilterOptions]
  );

  const debouncedSearchValue = useDebounce<string>(searchValue, 300);

  // Utils
  const debouncedLoadFilterOptions = useDebouncedFn(loadFilterOptions, 300);

  const handleFilterChange = useCallback(
    (fk: string, so: MenuMultiOption[]) => {
      setFilters((prev) =>
        prev.map((f) =>
          f.value === fk
            ? {
                ...f,
                options: f.options.map((o) => ({
                  ...o,
                  isChecked: so.some((_o) => _o.value === o.value),
                })),
              }
            : f
        )
      );
      setActiveFilters((prev) => ({
        ...prev,
        [fk]: so.map((_o) => {
          if (fk === F_KEY_ON || fk === F_KEY_AP) {
            return _o.label;
          }

          return _o.value;
        }),
      }));
    },
    []
  );

  const handleFooterLink2Click = useCallback((fk: string) => {
    setFilters((prev) =>
      prev.map((f) =>
        f.value === fk
          ? {
              ...f,
              options: f.options.map((o) => ({
                ...o,
                isChecked: false,
              })),
            }
          : f
      )
    );
    setActiveFilters((prev) => ({
      ...prev,
      [fk]: [],
    }));
  }, []);

  const handleClearBtn = useCallback(() => {
    setFilters((prev) =>
      prev.map((f) => ({
        ...f,
        options: f.options.map((o) => ({
          ...o,
          isChecked: false,
        })),
      }))
    );
    setActiveFilters({});
  }, []);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLUListElement>, fk: string) => {
      const target = e.target as HTMLUListElement;
      const { scrollTop, scrollHeight, clientHeight } = target;
      const targetFilter = filters.find((f) => f.value === fk);

      if (
        scrollHeight - scrollTop === clientHeight &&
        targetFilter?.hasMoreOptions
      ) {
        debouncedLoadFilterOptions(fk);
      }
    },
    [debouncedLoadFilterOptions, filters]
  );

  const handleClick = useCallback(
    (id: number) => {
      const modifiedList = listRows.map((item) => {
        if (item.tracker.id === id) {
          return {
            ...item,
            tracker: {
              ...item.tracker,
              isChecked: !item.tracker.isChecked,
            },
          };
        }

        return item;
      });

      setListRows(modifiedList);

      const currentItem = modifiedList.find((e) => e.tracker.id === id);
      const targetRow = activeRows.find((e) => e.tracker.id === id);

      if (targetRow && !currentItem?.tracker.isChecked) {
        setActiveRows((prev) => prev.filter((e) => !(e.tracker.id === id)));

        return;
      }

      if (!targetRow && currentItem?.tracker.isChecked) {
        setActiveRows((prev) => [...prev, currentItem]);
      }
    },
    [activeRows, listRows]
  );

  const handleSaveSAList = useCallback(() => {
    const addAccounts = activeRows
      .filter(
        (r) => !currentServiceAccounts.some((sa) => sa.id === r.accountKey)
      )
      .map((r) => ({
        accountKey: r.accountKey,
        accountName: r.name,
        accountType: r.accountType,
        description: r.description,
        endpointKey: r.endpointKey,
        endpointLogo: r.endpointLogo,
        endpointName: r.endpointName,
        status: r.status,
        isChecked: true,
      }));

    const removeAccounts = currentServiceAccounts.filter(
      (sa) => !activeRows.some((r) => r.accountKey === sa.id)
    );

    handleSubmit({
      addAccounts,
      removeAccounts,
      finalList: activeRows.map((r) => ({
        accountKey: r.accountKey,
        accountName: r.name,
        accountType: r.accountType,
        description: r.description,
        endpointKey: r.endpointKey,
        endpointLogo: r.endpointLogo,
        endpointName: r.endpointName,
        status: r.status,
        isChecked: true,
      })),
    });
  }, [activeRows, currentServiceAccounts, handleSubmit]);

  // Effects
  useEffect(() => {
    if (
      debouncedSearchValue.trim().length <= CHAR_LIMIT_FOR_SEARCH &&
      debouncedSearchValue.trim().length > 0
    ) {
      return;
    }

    setSearchText(debouncedSearchValue);
  }, [debouncedSearchValue]);

  useEffect(() => {
    filters.forEach((f) => {
      if (f.isApiBased) {
        loadFilterOptions(f.value, true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- want to run one time only
  }, []);

  // List management
  useEffect(() => {
    if (!listState.isObserverInView || !isValidApiCall) {
      return;
    }

    Logger.log('Fetching', listState.cursor);

    setListState((prev) => ({
      ...prev,
      isListLoading: true,
      isListError: false,
    }));

    const filteredActiveFilters = Object.fromEntries(
      Object.entries(activeFilters).filter(([, value]) => value.length > 0)
    );

    const payload = {
      serviceId,
      cursor: listState.cursor,
      max: 20,
      ...(searchText.length > 0 ? { search: searchText } : {}),
      ...(Object.keys(filteredActiveFilters).length > 0
        ? { filter: filteredActiveFilters }
        : {}),
    };

    fetchList(payload)
      .unwrap()
      .then((response) => {
        const { success, data, cursor, total } = response as {
          success: boolean;
          data: ServiceAccountState[] | null | undefined;
          cursor: string | null;
          total: number;
        };

        if (!success || !data) {
          setListState((prev) => ({
            ...prev,
            cursor: null,
            isListError: true,
          }));

          return;
        }

        const trackedResponse = data.map((item) => ({
          ...item,
          id: item.accountKey,
          tracker: {
            id: item.accountKey,
            isChecked: activeRows.find((o) => o.tracker.id === item.accountKey)
              ?.tracker.isChecked,
          },
        })) as unknown as TrackedListItem[];

        setListRows((prev) => [...prev, ...trackedResponse]);
        setListState((prev) => ({
          ...prev,
          cursor,
          totalCount: total,
        }));
      })
      .catch((error) => {
        Logger.log('Received error while fetching: ', error);

        setListState((prev) => ({
          ...prev,
          cursor: null,
          isListError: true,
        }));
      })
      .finally(() => {
        setListState((prev) => ({
          ...prev,
          isListLoading: false,
          isObserverInView: false,
        }));
      });
  }, [
    isValidApiCall,
    fetchList,
    listState.isObserverInView,
    listState.cursor,
    serviceId,
    searchText,
    activeFilters,
    activeRows,
  ]);

  // Resetting list and offset to default so that observer is visible
  useEffectAfterMount(() => {
    const debounceTimer = setTimeout(() => {
      setListState(DEFAULT_LIST_STATE);
      setListRows([]);
    }, 20);

    return () => clearTimeout(debounceTimer);
  }, [searchText, activeFilters]);

  useEffect(() => {
    if (activeRows.length === 0 && assignType === 2) {
      setAssignType(1);
    }
  }, [activeRows, assignType]);

  return (
    <ModalIsland
      isOpen={isModalOpen}
      onClose={handleModalClose}
      HeaderComp={
        <ModalPageHeader
          title={intl.formatMessage({
            id: 'phyalh',
            defaultMessage: 'Link Account(s)',
            description: 'Link Service Account Modal heading',
          })}
          closeHandler={handleModalClose}
          className='LinkAccountModal-headerWrapper'
        />
      }
      isOutsideClickEnabled={false}
      isCloseButtonVisible={!isLoading}
      isCloseOnEscapeKeyEnabled={!isLoading}
      contentBody={
        <div className='LinkAccountModal-contentWrapper'>
          <div className='LinkAccountModal-contentHeadingWrapper'>
            <Typography kind='h2' color={CF_NEU_BLD}>
              <FormattedMessage
                id='FvJVga'
                defaultMessage='Service Accounts'
                description='Heading for the list of service accounts in the Link Account modal'
              />
            </Typography>
            <Typography kind='body2' color={CF_NEU_STL}>
              <FormattedMessage
                id='1MSU62'
                defaultMessage='Limit is {maxCount}, you cannot add more service accounts.'
                description='Helper text in the service account modal that indicates the maximum number of service accounts'
                values={{ maxCount: MAX_ACCOUNT_ASSIGNMENT }}
              />
            </Typography>
          </div>
          <div className='LinkAccountModal-contentBodyWrapper'>
            <ToggleButtonGroup
              selection={assignType}
              setSelection={setAssignType}
              isDisabled={
                Boolean(isLoading || isDisabled) || listState.isListLoading
              }>
              <ToggleButton
                label={intl.formatMessage({
                  id: 'YXOeL5',
                  defaultMessage: 'Available',
                  description: 'Available',
                })}
                size='medium'
                isDisabled={
                  Boolean(isLoading || isDisabled) || listState.isListLoading
                }
              />
              <ToggleButton
                label={intl.formatMessage(
                  {
                    id: 'tRyPfV',
                    defaultMessage: 'Assigned ({count})',
                    description: 'Assigned',
                  },
                  { count: activeRows.length }
                )}
                size='medium'
                isDisabled={
                  isLoading ||
                  (isDisabled ?? false) ||
                  activeRows.length === 0 ||
                  listState.isListLoading
                }
              />
            </ToggleButtonGroup>
            <div className='LinkAccountModal-listControlsWrapper'>
              <div className='LinkAccountModal-listControls'>
                <InputField
                  id='link-account-search-input'
                  name='search'
                  size='medium'
                  placeholder={intl.formatMessage({
                    id: 'vQwyG6',
                    defaultMessage: 'Search by name, or description',
                    description: 'Search by name, or description',
                  })}
                  kind='search'
                  value={searchValue}
                  setValue={setSearchValue}
                  prefixIcon={
                    <Icon kind='Search' size='small' color={CF_NEU_STL} />
                  }
                  isCritical={
                    searchValue.trim().length <= CHAR_LIMIT_FOR_SEARCH &&
                    searchValue.trim().length > 0
                  }
                  isDisabled={
                    Boolean(isDisabled ?? isLoading) ||
                    assignType === 2 ||
                    isListError ||
                    listState.isListLoading
                  }
                  CriticalHelperText={
                    <InlineMessage
                      text={intl.formatMessage({
                        id: 'rMplSY',
                        defaultMessage:
                          'Type more than 2 characters to see results.',
                        description:
                          'Type more than 2 characters to see results.',
                      })}
                      colorTheme='critical'
                      size='small'
                      leftIcon={<Icon kind='AlertCriticalFilled' />}
                    />
                  }
                />
                <ButtonSelect
                  label={intl.formatMessage({
                    id: 'vYViQu',
                    defaultMessage: 'Filter',
                    description: 'Filter',
                  })}
                  onClick={() => {
                    setIsFilterBar((prev) => !prev);
                  }}
                  isOpen={isFilterBar}
                  isDisabled={
                    Boolean(isDisabled ?? isLoading) ||
                    assignType === 2 ||
                    listState.isListLoading
                  }
                  size='medium'
                  prefixIcon={<Icon kind='Filter' />}
                  BadgeComp={
                    isBadgeIcon ? (
                      <Badge kind='dot' color='primary' size='small' />
                    ) : undefined
                  }
                />
              </div>
              {isFilterBar && (
                <div className='LinkAccountModal-filterBar'>
                  {filters.map((f) => {
                    const noDataText = getNoDataText(
                      f.isError,
                      f.isLoading,
                      f.searchText,
                      f.options.length
                    );

                    return (
                      <MenuMulti
                        key={f.value}
                        label={intl.formatMessage(l[f.label as MessageState])}
                        options={
                          f.value !== F_KEY_ST
                            ? f.options
                            : f.options.map((o) => ({
                                ...o,
                                label: intl.formatMessage(
                                  l[o.label as MessageState]
                                ),
                              }))
                        }
                        trigger={
                          <ButtonSelect
                            kind='pill'
                            label=''
                            isDisabled={
                              Boolean(f.isLoading ?? isDisabled ?? isLoading) ||
                              assignType === 2 ||
                              listState.isListLoading
                            }
                          />
                        }
                        onChange={(so) => {
                          handleFilterChange(f.value, so ?? []);
                        }}
                        heightToShowScrollbar='16rem'
                        minOptionsForFooter={6}
                        minOptionsForSearch={
                          f.isApiBased ? 0 : f.options.length + 1
                        }
                        noDataText={
                          noDataText
                            ? intl.formatMessage(l[noDataText])
                            : undefined
                        }
                        onScroll={(e) => handleScroll(e, f.value)}
                        footerLink1Text=' '
                        footerLink2Text={intl.formatMessage({
                          id: 'sOTYaP',
                          defaultMessage: 'Clear Selection',
                          description: 'Clear Selection',
                        })}
                        onFooterLink2Click={() => {
                          handleFooterLink2Click(f.value);
                        }}
                        isFilteringDisabled
                        showOptionLabelOnHover
                        isLoading={f.isLoading}
                        hasMoreOptions={f.hasMoreOptions}
                        loadingText={intl.formatMessage({
                          id: 'KmuT+3',
                          defaultMessage: 'Loading...',
                          description: 'Loading',
                        })}
                        onSearchTermChange={(v: string | null) => {
                          setFilters((prev) =>
                            prev.map((_f) =>
                              _f.value === f.value
                                ? { ..._f, searchText: v ?? '' }
                                : _f
                            )
                          );

                          if (f.isApiBased) {
                            debouncedLoadFilterOptions(f.value, true);
                          }
                        }}
                      />
                    );
                  })}
                  {isBadgeIcon && (
                    <DSLink
                      text={intl.formatMessage({
                        id: 'e9OvzW',
                        defaultMessage: 'Clear',
                        description: 'Clear',
                      })}
                      kind='button'
                      onClick={handleClearBtn}
                      isDisabled={
                        isClrDisabled ||
                        Boolean(isDisabled ?? isLoading) ||
                        assignType === 2 ||
                        isListError ||
                        listState.isListLoading
                      }
                    />
                  )}
                </div>
              )}
            </div>
            <div className='LinkAccountModal-listRowWrapper'>
              {assignType === 1 &&
                listRows.map((item) => (
                  <ServiceAccountListItem
                    key={item.tracker.id}
                    intl={intl}
                    isLoading={listState.isListLoading}
                    listItem={item}
                    assignedCount={activeRows.length}
                    handleClick={handleClick}
                  />
                ))}
              {assignType === 2 &&
                activeRows.map((item) => (
                  <ServiceAccountListItem
                    key={item.tracker.id}
                    intl={intl}
                    isLoading={listState.isListLoading}
                    listItem={item}
                    assignedCount={activeRows.length}
                    handleClick={handleClick}
                  />
                ))}
              {listState.isListError && (
                <Box className='ListViewer-errorModal'>
                  <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
                  <Typography kind='body2' color={CF_CRT_MED}>
                    <FormattedMessage
                      id='apz7lc'
                      defaultMessage='System encountered an error. Please contact the Support Administrator'
                      description='System encountered an error. Please contact the Support Administrator'
                    />
                  </Typography>
                </Box>
              )}
              {/* Observer Element */}
              {!listState.isListError &&
              !listState.isListLoading &&
              assignType === 1
                ? listObserverElement
                : null}
              {/* Loading Skeleton */}
              {!listState.isListError && listState.isListLoading && (
                <Box className='ListViewer-loadingModal'>
                  <ListSkeleton count={3} />
                </Box>
              )}
              {!listState.isListError && isZeroCount && (
                <Box tag='div' className='AccessModal-illustrationWrapper'>
                  <Illustration kind='Empty' />
                  <Typography kind='h1'>
                    {isFilterApplied ? (
                      <FormattedMessage
                        id='C0QjhC'
                        defaultMessage='No Results Found!'
                        description='No Results Found!'
                      />
                    ) : (
                      <FormattedMessage
                        id='3uRK0t'
                        defaultMessage='There are no eligible Accounts to select!'
                        description='There are no eligible Accounts to select!'
                      />
                    )}
                  </Typography>
                </Box>
              )}
            </div>
          </div>
        </div>
      }
      className='LinkAccountModal'
      classNames={{
        sectionClassName: 'LinkAccountModal-sectionClass',
        bodyClassName: 'LinkAccountModal-bodyClass',
      }}
      FooterActionBarComp={
        <FooterActionBar size='large'>
          <div />
          <div className='LinkAccountModal-footerBtnWrapper'>
            <Button
              kind='ghost'
              isDisabled={isLoading || isDisabled}
              onClick={handleModalClose}>
              <FormattedMessage
                id='6PdOcy'
                defaultMessage='Cancel'
                description='Cancel'
              />
            </Button>
            <Button
              kind='filled'
              isDisabled={
                Boolean(isLoading || isDisabled) ||
                isListError ||
                listState.isListLoading
              }
              isLoading={isLoading}
              loaderText={intl.formatMessage({
                id: 'WmDyLh',
                defaultMessage: 'Saving...',
                description: 'Saving',
              })}
              onClick={handleSaveSAList}>
              <FormattedMessage
                id='0CvRZW'
                defaultMessage='Save'
                description='Save'
              />
            </Button>
          </div>
        </FooterActionBar>
      }
    />
  );
}

LinkAccountAction.defaultProps = {
  serviceId: undefined,
  isDisabled: false,
};

export default LinkAccountAction;
